import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { ScanLine, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Άνοιγμα κάμερας και σάρωση QR code.
 * Όταν διαβαστεί επιτυχώς, καλείται το onScan(decodedText).
 *
 * Αν το decoded text είναι URL της μορφής /cases/:id, το onScan
 * λαμβάνει είτε το full URL είτε καθαρό id (το decision γίνεται από
 * τον γονέα).
 */
const QRScannerDialog = ({ open, onOpenChange, onScan }) => {
  const scannerRef = useRef(null);
  const containerId = "qr-scanner-region";
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const html5Qr = new Html5Qrcode(containerId, { verbose: false });
    scannerRef.current = html5Qr;
    setError("");
    html5Qr
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          // Stop scanning before bubbling
          html5Qr
            .stop()
            .catch(() => {})
            .finally(() => {
              onScan(decodedText);
            });
        },
        () => {
          /* per-frame scan failures are normal — ignore */
        }
      )
      .catch((err) => {
        const msg = (err && err.message) || String(err);
        setError(msg);
        toast.error("Αποτυχία πρόσβασης στην κάμερα: " + msg);
      });

    return () => {
      try {
        if (html5Qr && html5Qr.getState && html5Qr.getState() === 2) {
          html5Qr.stop().catch(() => {});
        }
      } catch {
        /* noop */
      }
    };
  }, [open, onScan]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="w-5 h-5" /> Σάρωση QR Code
          </DialogTitle>
          <DialogDescription>
            Στρέψτε την κάμερα στο QR code της ετικέτας για άμεση μετάβαση στον φάκελο.
          </DialogDescription>
        </DialogHeader>
        <div id={containerId} className="w-full rounded-lg overflow-hidden bg-black" style={{ minHeight: 280 }} />
        {error && (
          <p className="text-sm text-red-600" data-testid="qr-scanner-error">{error}</p>
        )}
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="qr-scanner-close-btn">
            <X className="w-4 h-4 mr-1" /> Κλείσιμο
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRScannerDialog;
